import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ExportTemplate = {
  id: string;
  name: string;
  doctype: string;
  fields: string[];
  isDefault?: boolean;
};

/**
 * Default export templates — hardcoded per doctype.
 * Field names must match Frappe DocType meta fieldnames.
 * If a field doesn't exist in the tenant's meta, the ExportDialog
 * filters it out at render time (no crash).
 */
export const DEFAULT_EXPORT_TEMPLATES: ExportTemplate[] = [
  {
    id: "__default_so_order_summary",
    name: "Order Summary",
    doctype: "Sales Order",
    fields: [
      "name",
      "customer_name",
      "contact_email",
      "contact_phone",
      "transaction_date",
      "grand_total",
      "payment_status",
      "status",
      "shipping_address",
      "delivery_date",
    ],
    isDefault: true,
  },
  {
    id: "__default_so_fulfillment",
    name: "Fulfillment",
    doctype: "Sales Order",
    fields: [
      "name",
      "customer_name",
      "contact_phone",
      "contact_mobile",
      "shipping_address",
      "delivery_date",
      "status",
      "per_delivered",
      "fulfillment_priority",
      "storefront_order_notes",
    ],
    isDefault: true,
  },
  {
    id: "__default_so_customer_contact",
    name: "Customer Contact",
    doctype: "Sales Order",
    fields: [
      "name",
      "customer_name",
      "contact_email",
      "contact_phone",
      "contact_mobile",
      "customer_address",
      "address_display",
      "shipping_address",
      "territory",
    ],
    isDefault: true,
  },
  {
    id: "__default_so_marketplace",
    name: "Marketplace",
    doctype: "Sales Order",
    fields: [
      "name",
      "customer_name",
      "source_channel",
      "status",
      "grand_total",
      "transaction_date",
      "shopee_order_sn",
      "shopee_order_status",
      "shopee_tracking_no",
      "lazada_order_id",
      "lazada_order_status",
      "tiktok_order_id",
    ],
    isDefault: true,
  },
  {
    id: "__default_so_vat_invoice",
    name: "VAT Invoice",
    doctype: "Sales Order",
    fields: [
      "name",
      "customer_name",
      "transaction_date",
      "grand_total",
      "total_taxes_and_charges",
      "vat_invoice_requested",
      "vat_invoice_type",
      "vat_tax_id",
      "vat_company_name",
      "vat_branch_number",
      "vat_national_id",
      "vat_individual_name",
      "vat_email",
      "vat_billing_address",
    ],
    isDefault: true,
  },
  {
    id: "__default_so_accounting",
    name: "Accounting",
    doctype: "Sales Order",
    fields: [
      "name",
      "customer_name",
      "transaction_date",
      "grand_total",
      "net_total",
      "total_taxes_and_charges",
      "discount_amount",
      "coupon_code",
      "advance_paid",
      "payment_status",
      "status",
      "per_billed",
      "currency",
    ],
    isDefault: true,
  },
];

type ExportTemplateState = {
  userTemplates: ExportTemplate[];
  saveTemplate: (template: Omit<ExportTemplate, "id">) => void;
  deleteTemplate: (id: string) => void;
};

export const useExportTemplateStore = create<ExportTemplateState>()(
  persist(
    (set) => ({
      userTemplates: [],

      saveTemplate: (template) =>
        set((state) => ({
          userTemplates: [
            ...state.userTemplates,
            { ...template, id: `user_${Date.now()}` },
          ],
        })),

      deleteTemplate: (id) =>
        set((state) => ({
          userTemplates: state.userTemplates.filter((t) => t.id !== id),
        })),
    }),
    { name: "zvg.exportTemplates" },
  ),
);
